import {FC, useMemo} from 'react';
import {entries, PoiKey} from 'src/types/general';
import {Typography} from '@components/Typography';
import {FormattedMessage} from 'react-intl';
import {ParamsTable} from '@components/ParamsTable';
import {Section} from '@components/Section';
import {InfoTooltip} from '@components/InfoTooltip';
import {Icon} from '@components/Icon';
import {Feature, Point} from 'geojson';
import {Locale} from ".cache/__types__";

const additionalParameters = {
    internet: {
        label: (advert: AdditionalParametersProps['advert']) => (
            <a href={advert.formattedAds.find(a => a.name === "internet")?.valueHref}>
                <InfoTooltip
                    content={advert.formattedAds.find(a => a.name === "internet")?.value}>
                    <FormattedMessage defaultMessage={'Internet'}/>
                </InfoTooltip>
            </a>
        ),
        icon: <Icon.Internet/>,
        show: (advert: AdditionalParametersProps['advert']) => advert.formattedAds.some(a => a.name === "internet"),
    },
    energy: {
        label: (advert: AdditionalParametersProps['advert']) => (
            <a href={advert.formattedAds.find(a => a.name === "cez")?.valueHref}>
                <InfoTooltip
                    content={advert.formattedAds.find(a => a.name === "cez")?.value}>
                    <FormattedMessage defaultMessage={'Energie'}/>
                </InfoTooltip>
            </a>
        ),
        icon: <Icon.Energy/>,
        show: (advert: AdditionalParametersProps['advert']) => advert.formattedAds.some(a => a.name === "cez"),
    },
    estimation: {
        label: (advert: AdditionalParametersProps["advert"]) => (
            <a href={advert.formattedAds.find(a => a.name === "estimation")?.valueHref}>
                    {advert.formattedAds.find(a => a.name === "estimation")?.value}
            </a>
        ),
        icon: <Icon.Energy/>,
        show: (advert: AdditionalParametersProps['advert']) => advert.formattedAds.some(a => a.name === "estimation"),
    },
    balcony: {
        icon: <Icon.Balcony/>,
        label: <FormattedMessage defaultMessage={'Balkón'}/>,
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('balcony') && advert?.balcony,
    },
    terrace: {
        icon: <Icon.Balcony/>,
        label: <FormattedMessage defaultMessage={'Terasa'}/>,
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('terrace') && advert?.terrace,
    },
    newBuilding: {
        icon: <Icon.Balcony/>,
        label: <FormattedMessage defaultMessage={'Novostavba'}/>,
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('newBuilding') && advert?.newBuilding,
    },
    cellar: {
        icon: <Icon.Balcony/>,
        label: <FormattedMessage defaultMessage={'Sklep'}/>,
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('cellar') && advert?.cellar,
    },
    loggia: {
        icon: <Icon.Balcony/>,
        label: <FormattedMessage defaultMessage={'Lodžie'}/>,
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('loggia') && advert?.loggia,
    },
    parking: {
        icon: <Icon.Parking/>,
        label: <FormattedMessage defaultMessage={'Parkování'}/>,
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('parking') && advert?.parking,
    },
    lift: {
        icon: <Icon.Elevator/>,
        label: <FormattedMessage defaultMessage={'Výtah'}/>,
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('lift') && advert?.lift,
    },
    garage: {
        icon: <Icon.Garage/>,
        label: <FormattedMessage defaultMessage={'Garáž'}/>,
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('garage') && advert?.garage,
    },
    frontGarden: {
        icon: <Icon.Balcony/>,
        label: (advert: AdditionalParametersProps['advert']) => (
            <FormattedMessage defaultMessage={'Předzahrádka {surface, number} m²'}
                              values={{surface: advert.frontGarden}}/>
        ),
        show: (advert: AdditionalParametersProps['advert']) => advert?.hasOwnProperty('frontGarden') && advert?.frontGarden,
    },
    publicTransport: {
        icon: <Icon.PublicTransport/>,
        label: (advert: AdditionalParametersProps['advert']) => {
            const poiData: Record<PoiKey, Feature<Point>> = JSON.parse(advert.poiData ?? '');
            const type = poiData.public_transport.properties?.walkDuration > 300 ? 'car' : 'walk';

            return (
                <FormattedMessage
                    defaultMessage={'MHD {time, number, ::unit/minute unit-width-full-name .} {type, select, car {autem} other {pěšky}}'}
                    values={{
                        time:
                            (type === 'car'
                                ? poiData.public_transport.properties?.carDuration
                                : poiData.public_transport.properties?.walkDuration) / 60,
                        type,
                    }}
                />
            );
        },
        show: (advert: AdditionalParametersProps['advert']) => {
            if (!advert.poiData) {
                return false;
            }
            const poiData: Record<PoiKey, Feature<Point>> = JSON.parse(advert.poiData);
            return !!poiData?.public_transport;
        },
    },
};

export type AdditionalParametersProps = {
    advert: {
        balcony?: boolean;
        terrace?: boolean;
        newBuilding?: boolean;
        cellar?: boolean;
        loggia?: boolean;
        parking?: boolean;
        lift?: boolean;
        garage?: boolean;
        frontGarden?: number;
        poiData?: string;
        locale: Locale;
        formattedAds: {
            name: string;
            value: string;
            valueHref: string;
        }[]
    };
};

export const AdditionalParameters: FC<AdditionalParametersProps> = ({advert}) => {
    const availableAdditionalParameters = useMemo(() => {
        return entries(additionalParameters).filter(([_, parameter]) => parameter.show(advert));
    }, [advert]);

    return (
        <Section>
            <Typography as="h2" className="mb-3 mb-lg-8" variant="subheadline">
                <FormattedMessage defaultMessage={'Co tato nemovitost nabízí?'}/>
            </Typography>
            <ParamsTable>
                <ParamsTable.Group>
                    <table>
                        <tbody>
                        {availableAdditionalParameters
                            .filter((_, idx) => idx % 2 === 0)
                            .map(([key, parameter]) => (
                                <tr key={key}>
                                    <th>{parameter.icon}</th>
                                    <td>{typeof parameter.label === 'function' ? parameter.label(advert) : parameter.label}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ParamsTable.Group>
                <ParamsTable.Group>
                    <table>
                        <tbody>
                        {availableAdditionalParameters
                            .filter((_, idx) => idx % 2 === 1)
                            .map(([key, parameter]) => (
                                <tr key={key}>
                                    <th>{parameter.icon}</th>
                                    <td>{typeof parameter.label === 'function' ? parameter.label(advert) : parameter.label}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </ParamsTable.Group>
            </ParamsTable>
        </Section>
    );
};
